/**
 * 將金額格式化為新台幣整數顯示字串
 * 範例：formatNTD(280) → "NT$280"
 *       formatNTD(1250) → "NT$1,250"
 *
 * @param amount 金額數值
 * @returns 格式化後的新台幣字串
 */
export function formatNTD(amount: number): string {
  const rounded = Math.round(amount);
  return `NT$${rounded.toLocaleString('zh-TW')}`;
}

/**
 * 使用 Intl.DateTimeFormat 將日期格式化為 zh-TW 地區字串
 * 預設格式為 YYYY/MM/DD
 *
 * @param date 日期（Date 物件或 ISO 字串）
 * @param options Intl.DateTimeFormatOptions 選項
 * @returns 格式化後的日期字串
 */
export function formatDate(
  date: Date | string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };

  return new Intl.DateTimeFormat('zh-TW', options ?? defaultOptions).format(d);
}

/**
 * 產生 URL 友善的 slug
 * 規則：轉小寫、空白與特殊字元轉連字號、保留中文字元、移除多餘連字號
 *
 * @param text 原始文字
 * @returns slug 字串
 */
export function generateSlug(text: string): string {
  return text
    .trim()
    .toLowerCase()
    // 將空白、底線、斜線、句點等替換為連字號
    .replace(/[\s_/.:]+/g, '-')
    // 移除非英數字、非中文、非連字號的字元
    .replace(/[^a-z0-9一-鿿-]/g, '')
    // 合併多個連續連字號為一個
    .replace(/-{2,}/g, '-')
    // 移除開頭與結尾的連字號
    .replace(/^-+|-+$/g, '');
}

/**
 * 將字串截斷至指定最大長度，超出部分以「...」替代
 * 若字串長度未超過 maxLength，則原樣回傳
 *
 * @param text 原始字串
 * @param maxLength 最大長度（不含「...」）
 * @returns 截斷後的字串
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + '...';
}
